<template>
  <div :class="cardClasses" @click="handleClick">
    <div v-if="$slots.header || title" class="intercom-card-header">
      <slot name="header">
        <h3 class="intercom-card-title">{{ title }}</h3>
      </slot>
    </div>

    <div class="intercom-card-body">
      <slot />
    </div>

    <div v-if="$slots.footer" class="intercom-card-footer">
      <slot name="footer" />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  /**
   * Card title (shown in header if no header slot)
   */
  title: {
    type: String,
    default: '',
  },
  /**
   * Enable hover effect (lift + blue border)
   */
  hoverable: {
    type: Boolean,
    default: false,
  },
  /**
   * Make card clickable
   */
  clickable: {
    type: Boolean,
    default: false,
  },
  /**
   * Remove padding from body
   */
  noPadding: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['click'])

const cardClasses = computed(() => [
  'intercom-card',
  {
    'intercom-card--hoverable': props.hoverable,
    'intercom-card--clickable': props.clickable,
    'intercom-card--no-padding': props.noPadding,
  },
])

const handleClick = (event) => {
  if (props.clickable) {
    emit('click', event)
  }
}
</script>

<style scoped>
/* ========================================
   INTERCOM CARD BASE
   ======================================== */
.intercom-card {
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-lg);
  overflow: hidden;
  transition: all var(--transition-base);
}

.intercom-card--hoverable {
  cursor: pointer;
}

.intercom-card--hoverable:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-lg);
  border-color: var(--color-blue);
}

.intercom-card--clickable {
  cursor: pointer;
}

.intercom-card-header {
  padding: var(--space-5) var(--space-6);
  border-bottom: 1px solid var(--color-gray-200);
}

.intercom-card-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0;
}

.intercom-card-body {
  padding: var(--space-6);
}

.intercom-card--no-padding .intercom-card-body {
  padding: 0;
}

.intercom-card-footer {
  padding: var(--space-5) var(--space-6);
  border-top: 1px solid var(--color-gray-200);
  background: var(--color-gray-50);
}
</style>
